import JSZip from "jszip";
import type { Equipment } from "../api/equipment/Equipment.typed";

/*
 * QR canvas export
 * ----------------
 * All values below are in OUTPUT pixels (no logical/scale duality). The PNG is
 * rendered at this resolution directly so it stays crisp on a ~20 cm print.
 *
 * Layout:
 *   ┌──────────────────────────────────┐  ▲
 *   │            MARGIN (bg)           │  │
 *   │   ┌──────────────────────────┐   │  │
 *   │   │       fields frame       │   │  │  total height
 *   │   └──────────────────────────┘   │  │
 *   │              GAP (bg)            │  │
 *   │   ┌──────────────────────────┐   │  │
 *   │   │           QR             │   │  │
 *   │   └──────────────────────────┘   │  │
 *   │            MARGIN (bg)           │  ▼
 *   └──────────────────────────────────┘
 *   ◄──── MARGIN + QR_PX + MARGIN ────►
 */

/** Output pixel side length of the QR module (≈ 6 cm at 300 DPI / 4 cm at 200 DPI / 20 cm at 60 DPI). */
const QR_PX = 1200;
/** Colored mat on every side of the canvas. */
const MARGIN_PX = 60;
/** Vertical space between the fields frame and the QR. */
const GAP_PX = 24;
/** Padding inside the fields frame (top/bottom). */
const FRAME_VPAD_PX = 36;
/** Per-line height inside the fields frame. */
const FIELD_LINE_PX = 110;
/** Frame border width and color. */
const FRAME_STROKE_PX = 3;
const FRAME_STROKE_COLOR = "rgba(15, 23, 42, 0.45)";
/** Field text font (must fit within FIELD_LINE_PX). */
const FIELD_FONT = '600 72px "Public Sans", "Segoe UI", system-ui, sans-serif';
const FIELD_TEXT_COLOR = "#1e293b";
/** Vector logo overlay sizing ratios relative to QR_PX. */
const LOGO_SIZE_RATIO = 0.26;
const LOGO_COVER_RATIO = 0.3;

/** Exposed for the export modal (shows "QR <px> px" to the user). */
export const QR_EXPORT_QR_PX = QR_PX;

export interface QRExportImage {
  img: HTMLImageElement;
  eq: Equipment;
}

function frameHeight(lines: number): number {
  return lines === 0 ? 0 : 2 * FRAME_VPAD_PX + lines * FIELD_LINE_PX;
}

/** PNG dimensions in output pixels for the given number of field lines. */
export function getQRExportPixelSize(lines: number): {
  width: number;
  height: number;
} {
  const fH = frameHeight(lines);
  const stack = (fH > 0 ? fH + GAP_PX : 0) + QR_PX;
  return {
    width: QR_PX + 2 * MARGIN_PX,
    height: 2 * MARGIN_PX + stack,
  };
}

function getQRBackgroundColor(eq: Equipment): string {
  const qrCode = eq.QRCode;
  return typeof qrCode === "object" && qrCode?.BackgroundColor ? qrCode.BackgroundColor : "#ffffff";
}

/**
 * Detects the bounding box of QR modules (non-background pixels) inside the
 * source image so we can crop out the embedded quiet zone before drawing —
 * otherwise the surrounding fields frame visually exceeds the QR modules.
 *
 * Falls back to the full image if pixel data is unavailable (tainted canvas).
 */
function findQRContentBounds(img: HTMLImageElement): {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
} {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (w === 0 || h === 0) return { sx: 0, sy: 0, sw: w, sh: h };
  const probe = document.createElement("canvas");
  probe.width = w;
  probe.height = h;
  const pctx = probe.getContext("2d", { willReadFrequently: true });
  if (!pctx) return { sx: 0, sy: 0, sw: w, sh: h };
  pctx.drawImage(img, 0, 0);
  let data: ImageData;
  try {
    data = pctx.getImageData(0, 0, w, h);
  } catch {
    return { sx: 0, sy: 0, sw: w, sh: h };
  }
  const px = data.data;
  /* Background reference = top-left pixel. Tolerance covers anti-aliasing. */
  const br = px[0]!,
    bg = px[1]!,
    bb = px[2]!;
  const tol = 24;
  let minX = w,
    minY = h,
    maxX = -1,
    maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (
        Math.abs(px[i]! - br) > tol ||
        Math.abs(px[i + 1]! - bg) > tol ||
        Math.abs(px[i + 2]! - bb) > tol
      ) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return { sx: 0, sy: 0, sw: w, sh: h };
  return { sx: minX, sy: minY, sw: maxX - minX + 1, sh: maxY - minY + 1 };
}

/**
 * Loads the brand QR logo as a CORS-clean HTMLImageElement so the export
 * canvas can call `toDataURL` after drawing it. The bucket hosting the asset
 * MUST return `Access-Control-Allow-Origin` (e.g. Scaleway/S3 CORS rules),
 * otherwise the browser refuses the request and we fall back to no overlay.
 *
 * Cached per URL for the page lifetime.
 */
const logoCache = new Map<string, Promise<HTMLImageElement | null>>();

export function loadQRLogoImage(url: string | undefined | null): Promise<HTMLImageElement | null> {
  const trimmed = (url || "").trim();
  if (!trimmed) return Promise.resolve(null);
  const cached = logoCache.get(trimmed);
  if (cached) return cached;
  const promise = new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => {
      /* Most likely cause: bucket missing CORS headers — surface in console
       * so admins can diagnose without us swallowing the failure silently. */

      console.warn("[QR export] Could not load brand logo (CORS or 404?). URL:", trimmed);
      resolve(null);
    };
    img.src = trimmed;
  });
  logoCache.set(trimmed, promise);
  return promise;
}

/** Draws one equipment QR canvas (fields frame + colored mat + QR + optional vector logo). */
export function drawQRSingle(
  img: HTMLImageElement,
  eq: Equipment,
  exportAttrIds: string[],
  filenameAttrId?: string,
  logoImg?: HTMLImageElement | null,
): { dataUrl: string; baseName: string } {
  const lines = exportAttrIds.length;
  const fH = frameHeight(lines);
  const { width, height } = getQRExportPixelSize(lines);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = getQRBackgroundColor(eq);
  ctx.fillRect(0, 0, width, height);

  if (lines > 0) {
    ctx.strokeStyle = FRAME_STROKE_COLOR;
    ctx.lineWidth = FRAME_STROKE_PX;
    const half = FRAME_STROKE_PX / 2;
    ctx.strokeRect(
      MARGIN_PX + half,
      MARGIN_PX + half,
      QR_PX - FRAME_STROKE_PX,
      fH - FRAME_STROKE_PX,
    );

    ctx.font = FIELD_FONT;
    ctx.fillStyle = FIELD_TEXT_COLOR;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const cx = MARGIN_PX + QR_PX / 2;
    exportAttrIds.forEach((attrId, i) => {
      const text = `${eq.Attributes[attrId] ?? ""}`;
      const textY = MARGIN_PX + FRAME_VPAD_PX + (i + 0.5) * FIELD_LINE_PX;
      ctx.fillText(text, cx, textY);
    });
  }

  /* Keep QR modules sharp at any output size — no bilinear smoothing. */
  ctx.imageSmoothingEnabled = false;
  const qrY = MARGIN_PX + (fH > 0 ? fH + GAP_PX : 0);
  const { sx, sy, sw, sh } = findQRContentBounds(img);
  ctx.drawImage(img, sx, sy, sw, sh, MARGIN_PX, qrY, QR_PX, QR_PX);

  if (logoImg) {
    /* Mask the rasterized logo embedded by the backend so the vector overlay
     * sits on a clean square — relies on QR error correction (level H ≥ 30 %). */
    const cover = QR_PX * LOGO_COVER_RATIO;
    const logoSize = QR_PX * LOGO_SIZE_RATIO;
    const cx = MARGIN_PX + QR_PX / 2;
    const cy = qrY + QR_PX / 2;
    ctx.fillStyle = getQRBackgroundColor(eq);
    ctx.fillRect(cx - cover / 2, cy - cover / 2, cover, cover);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(logoImg, cx - logoSize / 2, cy - logoSize / 2, logoSize, logoSize);
  }

  const dataUrl = canvas.toDataURL("image/png");
  const baseFromSelected = exportAttrIds
    .map((id) => `${eq.Attributes[id] || ""}`)
    .filter(Boolean)
    .join("_");
  const baseFromChoice = filenameAttrId ? (eq.Attributes[filenameAttrId] || "").toString() : "";
  const baseName = (baseFromChoice || baseFromSelected || eq.ID || "qrcode")
    .toString()
    .replace(/\s+/g, "_");
  return { dataUrl, baseName };
}

const ZIP_NAME_INVALID = /[<>:"/\\|?*\u0000-\u001F]/g;

function uniquePngName(baseName: string, used: Set<string>): string {
  const stem = baseName.replace(ZIP_NAME_INVALID, "_") || "qrcode";
  let name = `${stem}.png`;
  let n = 1;
  while (used.has(name)) {
    name = `${stem}_${n++}.png`;
  }
  used.add(name);
  return name;
}

/** ZIP archive containing one PNG per equipment. */
export function buildQRExportZip(files: { dataUrl: string; baseName: string }[]): Promise<Blob> {
  const zip = new JSZip();
  const used = new Set<string>();
  for (const { dataUrl, baseName } of files) {
    const entry = uniquePngName(baseName, used);
    const b64 = dataUrl.split(",")[1] ?? "";
    if (b64) {
      zip.file(entry, b64, { base64: true });
    }
  }
  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}
